import React from 'react';
import cx from 'classnames';
import useId from '@mc/hooks/useId';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import ClusterLayout from '../ClusterLayout';
import Text from '../Text';
import { ariaDescribedByIds, ariaLabelledByIds } from '../utils';
import stylesheet from './Meter.css';

export type MeterProps = {
  action?: React.ReactNode;
  'aria-labelledby'?: string;
  className?: string;
  helpText?: React.ReactNode;
  hideLabel?: boolean;
  label?: React.ReactNode;
  max: number;
  min?: number;
  miscText?: React.ReactNode;
  /**
   * xp.mcds_redesign_components_organisms cleanup
   * remove large and tiny as they will no longer be options and aren't found
   * anywhere in the app
   */
  size?: 'large' | 'medium' | 'small' | 'tiny';
  value: number;
  variant?:
    | ('neutral' | 'positive' | 'warning' | 'negative')
    | $TSFixMeFunction;
};

const Meter = React.forwardRef<$TSFixMe, MeterProps>(function Meter(
  {
    'aria-labelledby': ariaLabelledBy,
    className,
    action,
    hideLabel,
    helpText,
    miscText,
    label,
    size = 'medium',
    min = 0,
    max = 100,
    value,
    variant = 'neutral',
  },
  forwardedRef,
) {
  const id = useId();
  const labelId = useId();
  const helpTextId = useId();
  const miscTextId = useId();

  const calculatedFillWidth = (value - min) / (max - min);
  const presentedFillWidth =
    Math.max(0, Math.min(1, calculatedFillWidth)) * 100;

  const fillModifierClass =
    presentedFillWidth >= 100
      ? stylesheet.meterFull
      : stylesheet.meterPartialFill;

  const meterColor =
    typeof variant === 'function' ? variant(presentedFillWidth) : variant;

  const fillWidth = (value / (max - min)) * 100;

  const _variant = typeof variant === 'function' ? variant(fillWidth) : variant;

  const textSize = size === 'tiny' ? 'small' : size;

  return mcdsFlagCheck('xp_mcds_redesign_components_organisms') ? (
    <div
      className={cx(stylesheet.root, stylesheet[size], className)}
      ref={forwardedRef}
    >
      <div className={stylesheet.before}>
        {label && (
          <Text className={stylesheet.label}>
            <span
              id={labelId}
              className={cx(hideLabel && 'wink-visually-hidden')}
            >
              {label}
            </span>
          </Text>
        )}
        {miscText && (
          <Text id={miscTextId} className={stylesheet.secondary}>
            {miscText}
          </Text>
        )}
      </div>
      <div
        id={id}
        role="progressbar"
        aria-labelledby={ariaLabelledByIds(ariaLabelledBy, label && labelId)}
        aria-describedby={ariaDescribedByIds(
          helpText && helpTextId,
          miscText && miscTextId,
        )}
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
        className={cx(stylesheet.meterTrack, stylesheet[meterColor])}
      >
        <div
          className={cx(stylesheet.meterFill, fillModifierClass)}
          style={{
            width: presentedFillWidth + '%',
          }}
        />
      </div>

      <div className={stylesheet.after}>
        {helpText && (
          <Text as="span" id={helpTextId} className={stylesheet.helpText}>
            {helpText}
          </Text>
        )}
        <div className={stylesheet.action}>{action}</div>
      </div>
    </div>
  ) : (
    <div className={cx(stylesheet.root, className)} ref={forwardedRef}>
      <div className={stylesheet.before}>
        {label && (
          <span
            className={cx(
              'mcds-label-default',
              hideLabel && 'wink-visually-hidden',
            )}
            id={labelId}
          >
            <Text
              as="span"
              appearance={
                textSize === 'large' ? 'heading-3' : `${textSize}-bold`
              }
              className={stylesheet.label}
            >
              {label}
            </Text>
          </span>
        )}
        {miscText && (
          <Text id={miscTextId} appearance={`${textSize}-secondary`}>
            {miscText}
          </Text>
        )}
      </div>

      <div
        id={id}
        role="progressbar"
        aria-labelledby={ariaLabelledByIds(ariaLabelledBy, label && labelId)}
        aria-describedby={ariaDescribedByIds(
          helpText && helpTextId,
          miscText && miscTextId,
        )}
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
        className={cx(
          stylesheet.meterTrack,
          stylesheet[size],
          stylesheet[_variant],
        )}
      >
        <div
          className={stylesheet.meterFill}
          style={{
            width: fillWidth + '%',
          }}
        />
      </div>

      <ClusterLayout
        justifyContent="space-between"
        className={stylesheet.after}
      >
        {size !== 'tiny' && helpText ? (
          <Text
            as="span"
            id={helpTextId}
            appearance="small"
            className={stylesheet.helpText}
          >
            {helpText}
          </Text>
        ) : null}
        {size !== 'tiny' && action ? (
          <Text as="span" appearance="small" className={stylesheet.action}>
            {action}
          </Text>
        ) : null}
      </ClusterLayout>
    </div>
  );
});

export default Meter;
